import { zodResolver } from "@hookform/resolvers/zod";
import { DOCS_FEEDBACK_RATING_TYPES } from "core/consts";
import { DocsFieldFeedback } from "core/types";
import { Button, ControlledTextarea, Form } from "ds/ui";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { ThumbsDownIcon, ThumbsUpIcon, XIcon } from "lucide-react";
import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useTranslations } from "translations";
import { z } from "zod";

const feedbackFormSchema = z.object({
  description: z.string().min(1),
});

type FeedbackFormValues = z.infer<typeof feedbackFormSchema>;

type DocsFeedbackPanelProps = {
  activeFeedback: DocsFieldFeedback;
  onClose: () => void;
  onSave: (description: string) => void;
};

export function DocsFeedbackPanel({
  activeFeedback,
  onClose,
  onSave,
}: DocsFeedbackPanelProps) {
  const translations = useTranslations();
  const toast = useToast();
  const methods = useForm<FeedbackFormValues>({
    defaultValues: {
      description: activeFeedback.ratingDescription ?? "",
    },
    resolver: zodResolver(feedbackFormSchema),
  });

  useEffect(() => {
    methods.reset({ description: activeFeedback.ratingDescription ?? "" });
  }, [activeFeedback.ratingDescription, activeFeedback.rating, methods]);

  const isPositive =
    activeFeedback.rating === DOCS_FEEDBACK_RATING_TYPES.POSITIVE;
  const textLabel = isPositive
    ? `${activeFeedback.label} - ${translations.extractDocs.userFeedback.fieldLabelPositive} `
    : `${activeFeedback.label} - ${translations.extractDocs.userFeedback.fieldLabelNegative}`;

  const handleSubmit = methods.handleSubmit(({ description }) => {
    onSave(description);
    toast({
      color: "success",
      message: translations.extractDocs.userFeedback.toastMessageSuccess,
    });
  });

  return (
    <FormProvider {...methods}>
      <Form
        className="flex h-full w-[50%] min-w-0 flex-col gap-3 overflow-hidden border-l border-gray-400 p-6"
        onSubmit={handleSubmit}
      >
        <div className="flex w-full justify-between pb-4">
          <div className="flex gap-2">
            {isPositive ? (
              <ThumbsUpIcon className="text-success" size={20} />
            ) : (
              <ThumbsDownIcon className="text-danger" size={20} />
            )}
            <h3 className="heading-md font-semibold">
              {translations.extractDocs.userFeedback.titleProvideFeedback}
            </h3>
          </div>
          <Button
            aria-label={translations.actions.close}
            color="black"
            id="close-feedback"
            isIconOnly
            size="sm"
            type="button"
            variant="light"
            onPress={onClose}
          >
            <XIcon size={14} />
          </Button>
        </div>
        <ControlledTextarea
          key={activeFeedback.rating}
          label={textLabel}
          name="description"
          placeholder={
            isPositive
              ? translations.extractDocs.userFeedback.fieldPlaceholderPositive
              : translations.extractDocs.userFeedback.fieldPlaceholderNegative
          }
          size="sm"
        />
        <div className="flex w-full justify-end gap-2 pt-2">
          <Button
            id="cancel-feedback"
            type="button"
            variant="light"
            onPress={onClose}
          >
            {translations.actions.cancel}
          </Button>
          <Button
            color="primary"
            id="save-feedback"
            isDisabled={!methods.formState.isValid}
            type="submit"
          >
            {translations.extractDocs.userFeedback.submitWrittenFeedback}
          </Button>
        </div>
      </Form>
    </FormProvider>
  );
}
